// screens/payment/PaymentScreen.js
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Alert,
  ActivityIndicator,
  SafeAreaView,
  StatusBar,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { StripeProvider } from '@stripe/stripe-react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import StripePaymentIntegration from './ProfessionalStripePayment';
import PaymentService from '../../services/PaymentService';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import { COMMERCE_DISABLED, CommerceUnavailable } from '../../utils/iosCommerce';

// The Stripe publishable key is NOT hardcoded here. It is fetched at runtime from
// the backend (GET /api/stripe/config), so the only place any Stripe key lives is
// the server .env. (The publishable key is public/safe by design — this just keeps
// all config in one place.)
const STRIPE_CONFIG_URL = 'https://api.jobeng.ai/api/stripe/config';
const JOBENG_API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const PaymentScreen = ({ route, navigation }) => {
  if (COMMERCE_DISABLED) return <CommerceUnavailable navigation={navigation} />;
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  const { scenario } = route.params || {};
  const [loading, setLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [publishableKey, setPublishableKey] = useState(null);
  const [keyError, setKeyError] = useState(false);

  useEffect(() => {
    loadUserData();
    loadStripeKey();
  }, []);

  const loadStripeKey = async () => {
    try {
      const res = await fetch(STRIPE_CONFIG_URL, {
        headers: { 'X-API-Key': JOBENG_API_KEY },
      });
      const data = await res.json();
      if (data && data.publishableKey) {
        setPublishableKey(data.publishableKey);
        setKeyError(false);
      } else {
        console.error('Stripe config response missing publishableKey:', data);
        setKeyError(true);
      }
    } catch (e) {
      console.error('Failed to load Stripe config:', e);
      setKeyError(true);
    }
  };

  const loadUserData = async () => {
    const user = await AsyncStorage.getItem('user_data');
    if (user) {
      setUserData(JSON.parse(user));
    }
  };

  if (!scenario) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#dc2626" />
        <LinearGradient colors={['#dc2626', '#b91c1c']} style={styles.errorHeader}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.errorHeaderTitle}>Payment Error</Text>
          <View style={{ width: 40 }} />
        </LinearGradient>
        <View style={styles.errorContainer}>
          <MaterialCommunityIcons name="alert-circle-outline" size={80} color="#dc2626" />
          <Text style={styles.errorTitle}>Something went wrong</Text>
          <Text style={styles.errorText}>No payment scenario provided</Text>
          <TouchableOpacity
            style={styles.errorButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.errorButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  const handlePaymentSuccess = async (result) => {
    try {
      setLoading(true);
      
      // Create subscription in backend
      if (scenario.type === 'subscription') {
        // SAFETY: do NOT fall back to a literal 1. A missing business id here
        // would otherwise have routed the new subscription to the test tenant
        // (company 1). If we genuinely don't know the business id, bail out
        // and ask the user to re-authenticate instead of writing bad data.
        const businessId = userData?.company_id || userData?.id || null;
        if (!businessId || businessId <= 0) {
          Alert.alert(
            'Subscription Sync Failed',
            'We could not identify your business account. Your payment went through — please contact support to activate your subscription.'
          );
          return;
        }
        await PaymentService.createBusinessSubscription(
          businessId,
          scenario.plan_id,
          scenario.interval
        );
      }

      // ── Course purchase: enroll the user in the backend, then route to learning ──
      if (scenario.type === 'course') {
        const courseId = scenario.metadata?.course_id;
        const userId   = profileCtx?.userId
                      || userData?.id
                      || userData?.user_id
                      || scenario.metadata?.user_id
                      || null;

        if (userId && courseId) {
          try {
            const token = await AsyncStorage.getItem('auth_token')
                       || await AsyncStorage.getItem('userToken')
                       || '';
            await fetch('https://api.jobeng.ai/api/courses/enroll', {
              method: 'POST',
              headers: {
                'Content-Type':  'application/json',
                'X-API-Key':     '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify({
                user_id:           userId,
                course_id:         courseId,
                payment_intent_id: result?.paymentIntent?.id || null,
                amount_in_pence:   result?.amount || scenario.amountInPence,
                currency:          result?.currency || scenario.currency,
                metadata:          scenario.metadata || {},
              }),
            });
          } catch (e) {
            console.error('Course enrollment sync failed (payment succeeded):', e);
            // We deliberately do NOT block the user — payment went through.
            // The backend can reconcile via the Stripe webhook.
          }
        }

        Alert.alert(
          '🎉 Enrolled!',
          `You have been enrolled in "${scenario.name}".`,
          [{
            text: 'Start Learning',
            onPress: () => {
              const route  = scenario.successRoute  || 'CourseLearning';
              const params = scenario.successParams || { courseId };
              // Use replace so the user can't navigate back into the payment screen
              if (typeof navigation.replace === 'function') {
                navigation.replace(route, params);
              } else {
                navigation.navigate(route, params);
              }
            },
          }]
        );
        return;
      }

      // Generic success (books, live courses, one-off products, or subscriptions
      // without a dedicated branch). If the scenario specifies where to go next,
      // route there; otherwise fall back to the payments list.
      if (scenario.successRoute) {
        Alert.alert(
          '🎉 Payment Successful!',
          scenario.successMessage || `Your ${scenario.name} purchase is complete.`,
          [{
            text: 'Continue',
            onPress: () => {
              const dest = scenario.successRoute;
              const params = scenario.successParams || {};
              // Forward flows (course → lesson) replace so Back doesn't return to
              // payment. Return-to-same-screen flows pass successReplace:false so the
              // originating screen keeps its in-memory state (e.g. a booking form).
              if (scenario.successReplace !== false && typeof navigation.replace === 'function') {
                navigation.replace(dest, params);
              } else {
                navigation.navigate(dest, params);
              }
            },
          }]
        );
        return;
      }

      Alert.alert(
        '🎉 Payment Successful!',
        `Your ${scenario.name} has been activated.`,
        [
          {
            text: 'View Subscription',
            onPress: () => navigation.navigate('PaymentsScreen'),
          },
        ]
      );
    } catch (error) {
      console.error('Error processing payment:', error);
      Alert.alert('Success', 'Payment completed but sync failed. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentFailure = (error) => {
    Alert.alert(
      'Payment Failed',
      error.message || 'Please try again or use a different payment method.',
      [
        { text: 'Try Again', style: 'default' },
        { text: 'Cancel', style: 'cancel', onPress: () => navigation.goBack() },
      ]
    );
  };

  if (!publishableKey) {
    return (
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        <LinearGradient
          colors={['#0e4e96', '#1a6bb0']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.header}
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Complete Payment</Text>
          <View style={{ width: 40 }} />
        </LinearGradient>
        <View style={styles.errorContainer}>
          {keyError ? (
            <>
              <MaterialCommunityIcons name="alert-circle-outline" size={80} color="#dc2626" />
              <Text style={styles.errorTitle}>Payment unavailable</Text>
              <Text style={styles.errorText}>
                We couldn't load the secure payment configuration. Please check your
                connection and try again.
              </Text>
              <TouchableOpacity
                style={styles.errorButton}
                onPress={() => { setKeyError(false); loadStripeKey(); }}
              >
                <Text style={styles.errorButtonText}>Retry</Text>
              </TouchableOpacity>
            </>
          ) : (
            <>
              <ActivityIndicator size={48} color="#0e4e96" />
              <Text style={[styles.errorText, { marginTop: 20 }]}>
                Loading secure payment…
              </Text>
            </>
          )}
        </View>
      </SafeAreaView>
    );
  }

  return (
    <StripeProvider publishableKey={publishableKey}>
      <SafeAreaView style={styles.container}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        
        <LinearGradient
          colors={['#0e4e96', '#1a6bb0']}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.header}
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Complete Payment</Text>
          <View style={{ width: 40 }} />
        </LinearGradient>

        <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
          <View style={styles.summaryCard}>
            <View style={styles.summaryHeader}>
              <View style={styles.iconContainer}>
                <MaterialCommunityIcons 
                  name={scenario.category === 'business' ? 'office-building' : 
                        scenario.category === 'professional' ? 'account-tie' : 'school'} 
                  size={32} 
                  color="#0e4e96" 
                />
              </View>
              <View style={styles.summaryInfo}>
                <Text style={styles.planName}>{scenario.name}</Text>
                <Text style={styles.planCategory}>{scenario.category} Plan</Text>
              </View>
            </View>

            <View style={styles.priceBreakdown}>
              <View style={styles.priceRow}>
                <Text style={styles.priceLabel}>Subtotal</Text>
                <Text style={styles.priceValue}>
                  £{(scenario.amountInPence / 100).toFixed(2)}
                </Text>
              </View>
              <View style={styles.priceRow}>
                <Text style={styles.priceLabel}>VAT (20%)</Text>
                <Text style={styles.priceValue}>
                  £{(scenario.amountInPence / 100 * 0.2).toFixed(2)}
                </Text>
              </View>
              <View style={[styles.priceRow, styles.totalRow]}>
                <Text style={styles.totalLabel}>Total</Text>
                <Text style={styles.totalValue}>
                  £{(scenario.amountInPence / 100 * 1.2).toFixed(2)}
                </Text>
              </View>
            </View>

            <View style={styles.billingInfo}>
              <Ionicons name="calendar-outline" size={20} color="#64748b" />
              <Text style={styles.billingText}>
                Billed {scenario.interval} • Cancel anytime
              </Text>
            </View>
          </View>

          {scenario.features && scenario.features.length > 0 && (
            <View style={styles.featuresCard}>
              <Text style={styles.featuresTitle}>What's included:</Text>
              {scenario.features.slice(0, 6).map((feature, index) => (
                <View key={index} style={styles.featureRow}>
                  <Ionicons name="checkmark-circle" size={22} color="#10B981" />
                  <Text style={styles.featureText}>{feature}</Text>
                </View>
              ))}
            </View>
          )}

          <StripePaymentIntegration
            scenario={scenario}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentFailure={handlePaymentFailure}
            onCancel={() => navigation.goBack()}
          />
        </ScrollView>

        {loading && (
          <View style={styles.loadingOverlay}>
            <ActivityIndicator size={48} color="#fff" />
            <Text style={styles.loadingOverlayText}>Processing payment...</Text>
          </View>
        )}
      </SafeAreaView>
    </StripeProvider>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8fafc',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
  },
  backButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'rgba(255,255,255,0.2)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  errorHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 20,
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
  },
  errorHeaderTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#fff',
  },
  content: {
    flex: 1,
    padding: 20,
  },
  summaryCard: {
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  summaryHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  iconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#e6f0ff',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  summaryInfo: {
    flex: 1,
  },
  planName: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  planCategory: {
    fontSize: 14,
    color: '#64748b',
    textTransform: 'capitalize',
  },
  priceBreakdown: {
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 16,
    marginBottom: 16,
  },
  priceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  priceLabel: {
    fontSize: 16,
    color: '#475569',
  },
  priceValue: {
    fontSize: 16,
    fontWeight: '500',
    color: '#0f172a',
  },
  totalRow: {
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    paddingTop: 12,
    marginTop: 4,
  },
  totalLabel: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  totalValue: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  billingInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    padding: 12,
    borderRadius: 12,
    gap: 8,
  },
  billingText: {
    fontSize: 14,
    color: '#475569',
  },
  featuresCard: {
    backgroundColor: '#fff',
    borderRadius: 20,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  featuresTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 16,
  },
  featureRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  featureText: {
    fontSize: 14,
    color: '#334155',
    marginLeft: 12,
    flex: 1,
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#0f172a',
    marginTop: 20,
    marginBottom: 8,
  },
  errorText: {
    fontSize: 16,
    color: '#64748b',
    textAlign: 'center',
    marginBottom: 24,
  },
  errorButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 12,
  },
  errorButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(14, 78, 150, 0.9)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingOverlayText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
    marginTop: 16,
  },
});

export default PaymentScreen;